import React, { useState, useEffect } from "react";
import { IonModal, IonIcon } from "@ionic/react";
import {
  closeOutline,
  chevronBackOutline,
  chevronForwardOutline,
  calendarOutline,
} from "ionicons/icons";

// Types
interface DateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDateRangeSelect?: (
    startDate: string,
    endDate: string,
    startISO: string,
    endISO: string
  ) => void;
  onDateSelect?: (date: string, dateISO: string) => void;

  // Customization
  title?: string;
  buttonText?: string;
  primaryGradient?: string;
  backgroundColor?: string;
  inputBackgroundColor?: string;
  mode?: "range" | "single";

  // Initial values
  initialStartDate?: string;
  initialEndDate?: string;

  // Constraints
  minDate?: string;
  maxDate?: string;

  // Validation
  customValidation?: (date: string) => boolean;

  // Styling
  modalWidth?: string;
  modalHeight?: string;
}

// Reusable IonDateRangeModal Component
const IonDateRangeModal: React.FC<DateRangeModalProps> = ({
  isOpen,
  onClose,
  onDateRangeSelect,
  onDateSelect,
  title = "Date range",
  buttonText = "Done",
  primaryGradient = "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
  backgroundColor = "#222222",
  inputBackgroundColor = "#262626",
  mode = "range",
  initialStartDate,
  initialEndDate,
  minDate,
  maxDate,
  customValidation,
  modalWidth = "362px",
  modalHeight = "auto",
}) => {
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Initialize selected dates
  useEffect(() => {
    if (isOpen) {
      const dates = [];
      if (initialStartDate) dates.push(initialStartDate);
      if (initialEndDate && mode === "range") dates.push(initialEndDate);
      setSelectedDates(dates);
    }
  }, [isOpen, initialStartDate, initialEndDate, mode]);

  // Date formatting utilities - no timezone conversion
  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    // Parse date directly without timezone conversion
    const [year, month, day] = dateStr.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateISO = `${year}-${month}-${day}`;

    // Check constraints
    if (minDate && dateISO < minDate) return;
    if (maxDate && dateISO > maxDate) return;
    if (customValidation && !customValidation(dateISO)) return;

    if (mode === "single") {
      setSelectedDates([dateISO]);
      return;
    }

    // Range mode logic
    if (selectedDates.length === 0) {
      setSelectedDates([dateISO]);
    } else if (selectedDates.length === 1) {
      const startDate = selectedDates[0];
      if (dateISO >= startDate) {
        setSelectedDates([selectedDates[0], dateISO]);
      } else {
        setSelectedDates([dateISO]);
      }
    } else {
      setSelectedDates([dateISO]);
    }
  };

  // Clear date functions
  const clearStartDate = () => {
    setSelectedDates([]);
  };

  const clearEndDate = () => {
    if (selectedDates.length > 1) {
      setSelectedDates([selectedDates[0]]);
    }
  };

  // Check if date is in range
  const isDateInRange = (date: Date) => {
    if (selectedDates.length < 2) return false;
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateISO = `${year}-${month}-${day}`;
    const start = selectedDates[0];
    const end = selectedDates[1];
    return dateISO >= start && dateISO <= end;
  };

  // Check if date is selected
  const isDateSelected = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateISO = `${year}-${month}-${day}`;
    return selectedDates.includes(dateISO);
  };

  // Check if date is start or end
  const isStartDate = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateISO = `${year}-${month}-${day}`;
    return selectedDates[0] === dateISO;
  };

  const isEndDate = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateISO = `${year}-${month}-${day}`;
    return selectedDates.length > 1 && selectedDates[1] === dateISO;
  };

  // Handle navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  // Handle done action
  const handleDone = () => {
    if (mode === "single" && selectedDates.length === 1) {
      const displayDate = formatDisplayDate(selectedDates[0]);
      const isoDate = selectedDates[0]; // Return the raw date string
      onDateSelect?.(displayDate, isoDate);
      onClose();
    } else if (mode === "range" && selectedDates.length === 2) {
      console.log("Selected Dates:", selectedDates);
      const startDisplay = formatDisplayDate(selectedDates[0]);
      const endDisplay = formatDisplayDate(selectedDates[1]);
      const startISO = selectedDates[0]; // Return the raw date string
      const endISO = selectedDates[1]; // Return the raw date string
      onDateRangeSelect?.(startDisplay, endDisplay, startISO, endISO);
      onClose();
    }
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      style={{
        "--background": "transparent",
        "--height": "auto",
        "--max-height": "80vh",
        "--overflow": "auto",
        "--box-shadow": "none",
      }}
    >
      <div
        className="flex items-center justify-center min-h-screen p-4"
        style={{ backgroundColor: "transparent" }}
      >
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            backgroundColor,
            width: modalWidth,
            maxWidth: "90vw",
            fontFamily:
              "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          {/* Header */}
          <div className="flex justify-between items-center px-6 py-4">
            <h2
              className="text-white text-2xl font-bold"
              style={{
                fontSize: "24px",
                lineHeight: "28px",
                fontWeight: "700",
              }}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center"
            >
              <IonIcon
                icon={closeOutline}
                className="text-gray-400"
                style={{ fontSize: "24px" }}
              />
            </button>
          </div>

          {/* Date Input Fields */}
          <div className="px-6 mb-6">
            {mode === "range" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                {/* Start Date */}
                <div
                  className="border rounded-xl p-4 relative"
                  style={{
                    backgroundColor: inputBackgroundColor,
                    border: "1px solid #FFFFFF",
                    borderRadius: "10px",
                    height: "46px",
                  }}
                >
                  <div className="flex justify-between items-center h-full">
                    <span
                      className="text-white text-sm "
                      style={{
                        fontSize: "14px",
                        lineHeight: "140%",
                        letterSpacing: "-0.01em",
                        fontWeight: "400",
                      }}
                    >
                      {selectedDates[0]
                        ? formatDisplayDate(selectedDates[0])
                        : "Start date"}
                    </span>
                    <div className="flex items-center gap-2">
                      {selectedDates[0] && (
                        <button
                          onClick={clearStartDate}
                          className="w-4 h-4 flex items-center justify-center"
                        >
                          <IonIcon
                            icon={closeOutline}
                            className="text-gray-400"
                            style={{ fontSize: "16px" }}
                          />
                        </button>
                      )}
                      <IonIcon
                        icon={calendarOutline}
                        className="text-white"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                  </div>
                </div>

                {/* End Date */}
                <div
                  className="border rounded-xl p-4 relative"
                  style={{
                    backgroundColor: inputBackgroundColor,
                    border: selectedDates[1]
                      ? "1px solid #FFFFFF"
                      : "1px solid #363636",
                    borderRadius: "10px",
                    height: "46px",
                  }}
                >
                  <div className="flex justify-between items-center h-full">
                    <span
                      className="text-white text-sm"
                      style={{
                        fontSize: "14px",
                        lineHeight: "140%",
                        letterSpacing: "-0.01em",
                        fontWeight: "400",
                      }}
                    >
                      {selectedDates[1]
                        ? formatDisplayDate(selectedDates[1])
                        : "End date"}
                    </span>
                    <div className="flex items-center gap-2">
                      {selectedDates[1] && (
                        <button
                          onClick={clearEndDate}
                          className="w-4 h-4 flex items-center justify-center"
                        >
                          <IonIcon
                            icon={closeOutline}
                            className="text-gray-400"
                            style={{ fontSize: "16px" }}
                          />
                        </button>
                      )}
                      <IonIcon
                        icon={calendarOutline}
                        className="text-white"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Single Date */
              <div
                className="border rounded-xl p-4 relative"
                style={{
                  backgroundColor: inputBackgroundColor,
                  border: "1px solid #FFFFFF",
                  borderRadius: "10px",
                  height: "46px",
                }}
              >
                <div className="flex justify-between items-center h-full">
                  <span
                    className="text-white text-sm"
                    style={{
                      fontSize: "14px",
                      lineHeight: "140%",
                      letterSpacing: "-0.01em",
                      fontWeight: "400",
                    }}
                  >
                    {selectedDates[0]
                      ? formatDisplayDate(selectedDates[0])
                      : "Select date"}
                  </span>
                  <div className="flex items-center gap-2">
                    {selectedDates[0] && (
                      <button
                        onClick={clearStartDate}
                        className="w-4 h-4 flex items-center justify-center"
                      >
                        <IonIcon
                          icon={closeOutline}
                          className="text-gray-400"
                          style={{ fontSize: "16px" }}
                        />
                      </button>
                    )}
                    <IonIcon
                      icon={calendarOutline}
                      className="text-white"
                      style={{ fontSize: "20px" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Calendar Navigation */}
          <div className="px-6 mb-4">
            <div className="flex justify-between items-center">
              <button
                onClick={goToPreviousMonth}
                className="w-11 h-11 flex items-center justify-center"
              >
                <IonIcon
                  icon={chevronBackOutline}
                  className="text-white"
                  style={{ fontSize: "20px" }}
                />
              </button>

              <span
                className="text-gray-300 text-sm font-semibold"
                style={{
                  color: "#B0B4B7",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {monthNames[currentMonth.getMonth()]}{" "}
                {currentMonth.getFullYear()}
              </span>

              <button
                onClick={goToNextMonth}
                className="w-11 h-11 flex items-center justify-center"
              >
                <IonIcon
                  icon={chevronForwardOutline}
                  className="text-white"
                  style={{ fontSize: "20px" }}
                />
              </button>
            </div>
          </div>

          {/* Calendar */}
          <div className="px-6 mb-6">
            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-0 mb-2">
              {weekDays.map((day, index) => (
                <div
                  key={day + index}
                  className="h-11 flex items-center justify-center"
                  style={{
                    color: "#B5B9BD",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0">
              {calendarDays.map((date, index) => {
                const isCurrentMonth =
                  date.getMonth() === currentMonth.getMonth();
                const isSelected = isDateSelected(date);
                const isStart = isStartDate(date);
                const isEnd = isEndDate(date);
                const isInRange = isDateInRange(date);
                const isToday =
                  date.toDateString() === new Date().toDateString();

                // Determine row and position for range styling
                const row = Math.floor(index / 7);
                const col = index % 7;
                const isFirstInRow = col === 0;
                const isLastInRow = col === 6;

                let rangeStyle = {};
                let dateStyle = {};

                if (mode === "range" && selectedDates.length === 2) {
                  if (isInRange && !isSelected) {
                    rangeStyle = {
                      backgroundColor: "#3B3D3F",
                      color: "#FFFFFF",
                    };
                  }

                  if (isStart) {
                    dateStyle = {
                      background: primaryGradient,
                      borderRadius: "50%",
                      color: "#FFFFFF",
                      fontWeight: "700",
                    };

                    // Range connection styling
                    if (!isLastInRow && selectedDates.length === 2) {
                      const nextDate = calendarDays[index + 1];
                      if (nextDate && isDateInRange(nextDate)) {
                        rangeStyle = {
                          ...rangeStyle,
                          backgroundColor: "#3B3D3F",
                          borderRadius: "0 30px 30px 0",
                        };
                      }
                    }
                  }

                  if (isEnd) {
                    dateStyle = {
                      background: primaryGradient,
                      borderRadius: "50%",
                      color: "#FFFFFF",
                      fontWeight: "700",
                    };
                  }
                }

                if (mode === "single" && isSelected) {
                  dateStyle = {
                    background: primaryGradient,
                    borderRadius: "50%",
                    color: "#FFFFFF",
                    fontWeight: "700",
                  };
                }

                return (
                  <div
                    key={index}
                    className="relative"
                    style={{
                      height: "46px",
                      ...rangeStyle,
                    }}
                  >
                    <button
                      onClick={() => handleDateSelect(date)}
                      disabled={!isCurrentMonth}
                      className="w-11 h-11 flex items-center justify-center relative z-10"
                      style={{
                        color: isCurrentMonth
                          ? isToday
                            ? "#01B7C4"
                            : "#6D7980"
                          : "#3B3D3F",
                        fontSize: "14px",
                        fontWeight: isToday ? "700" : "500",
                        borderRadius: "8px",
                        ...dateStyle,
                      }}
                    >
                      {date.getDate()}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Done Button */}
          <div className="px-6 pb-6">
            <button
              onClick={handleDone}
              disabled={
                mode === "single"
                  ? selectedDates.length === 0
                  : selectedDates.length < 2
              }
              className="w-full py-4 rounded-2xl font-semibold text-white disabled:opacity-50"
              style={{
                background: (
                  mode === "single"
                    ? selectedDates.length === 1
                    : selectedDates.length === 2
                )
                  ? primaryGradient
                  : "#666666",
                borderRadius: "30px",
                height: "46px",
                fontSize: "20px",
                lineHeight: "22px",
                fontWeight: "600",
                letterSpacing: "-0.408px",
              }}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .date-range-modal {
          --ion-backdrop-opacity: 0.6;
          --background: transparent !important;
        }
        
        .date-range-modal ion-backdrop {
          // display: none;
        }
        
        /* Responsive adjustments */
        @media (max-width: 480px) {
          .date-range-modal .calendar-container {
            font-size: 14px;
          }
        }
        
        @media (max-width: 375px) {
          .date-range-modal .calendar-container {
            font-size: 12px;
          }
          
          .date-range-modal .calendar-day {
            width: 32px;
            height: 32px;
            font-size: 12px;
          }
        }
        
        @media (max-width: 320px) {
          .date-range-modal .calendar-day {
            width: 28px;
            height: 28px;
            font-size: 11px;
          }
        }
      `}</style>
    </IonModal>
  );
};

// Example usage demonstrations
export const ExampleUsages: React.FC = () => {
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [showSingleModal, setShowSingleModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  return (
    <div className="p-6 space-y-4 bg-black min-h-screen">
      <h2 className="text-white text-xl font-bold mb-4">
        IonDateRangeModal Examples
      </h2>

      {/* Example 1: Standard Date Range Modal */}
      <button
        onClick={() => setShowRangeModal(true)}
        className="bg-blue-500 text-white px-4 py-2 rounded mr-4"
      >
        Open Date Range Modal
      </button>

      <IonDateRangeModal
        isOpen={showRangeModal}
        onClose={() => setShowRangeModal(false)}
        onDateRangeSelect={(start, end, startISO, endISO) => {
          console.log("Range selected:", { start, end, startISO, endISO });
          setShowRangeModal(false);
        }}
        title="Select Date Range"
        buttonText="Apply Range"
        mode="range"
      />

      {/* Example 2: Single Date Modal */}
      <button
        onClick={() => setShowSingleModal(true)}
        className="bg-green-500 text-white px-4 py-2 rounded mr-4"
      >
        Open Single Date Modal
      </button>

      <IonDateRangeModal
        isOpen={showSingleModal}
        onClose={() => setShowSingleModal(false)}
        onDateSelect={(date, dateISO) => {
          console.log("Date selected:", { date, dateISO });
          setShowSingleModal(false);
        }}
        title="Select Date"
        buttonText="Choose Date"
        mode="single"
        primaryGradient="linear-gradient(90deg, #22c55e 0%, #16a34a 100%)"
      />

      {/* Example 3: Custom Styled Modal */}
      <button
        onClick={() => setShowCustomModal(true)}
        className="bg-purple-500 text-white px-4 py-2 rounded mr-4"
      >
        Open Custom Styled Modal
      </button>

      <IonDateRangeModal
        isOpen={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        onDateRangeSelect={(start, end, startISO, endISO) => {
          console.log("Custom range selected:", {
            start,
            end,
            startISO,
            endISO,
          });
          setShowCustomModal(false);
        }}
        title="Custom Date Picker"
        buttonText="Confirm Selection"
        mode="range"
        primaryGradient="linear-gradient(90deg, #a855f7 0%, #7c3aed 100%)"
        backgroundColor="#1a1a2e"
        inputBackgroundColor="#16213e"
        modalWidth="400px"
        minDate="2024-01-01"
        maxDate="2025-12-31"
      />
    </div>
  );
};

export default IonDateRangeModal;
